import type {
  FetchTagByIdArgs,
  EditTagByIdArgs,
  CreateTagArgs,
  FetchTagsArgs,
} from './types/tags.types';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type { ApiTag, UiTag } from '../schemas/tags.schema';

import { mapTagFromApi, mapTagToApi } from './mappers/tags.mappers';
import { patch, post, get } from './axios';

export const fetchTags = async ({
  categoryId,
  filters,
  locale,
  organizationId,
  withPlatform,
}: FetchTagsArgs) => {
  const response = await get<BaseApiPaginatedResponse<ApiTag[]>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories/${categoryId}/tags`,
    {
      query: {
        ...filters,
        ...(organizationId && withPlatform ? { includePlatform: 'true' } : {}),
      },
    },
  );
  return {
    ...response,
    data: response?.data?.map((tag) => mapTagFromApi(locale, tag)) as UiTag[],
  };
};

export const fetchPublicTags = async ({
  categoryId,
  filters,
  locale,
}: Omit<FetchTagsArgs, 'organizationId' | 'withPlatform'>) => {
  const response = await get<BaseApiPaginatedResponse<ApiTag[]>>(
    `/v2/public/categories/${categoryId}/tags`,
    {
      query: {
        ...filters,
      },
    },
  );
  return {
    ...response,
    data: response?.data?.map((tag) => mapTagFromApi(locale, tag)) as UiTag[],
  };
};

export async function fetchTagById({
  id,
  categoryId,
  locale,
  organizationId,
}: FetchTagByIdArgs): Promise<UiTag | null> {
  const response = await get<BaseApiResponse<ApiTag>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories/${categoryId}/tags/${id}`,
  );
  if (!response?.data) return null;
  return mapTagFromApi(locale, response?.data);
}

export async function createTag({
  categoryId,
  locale,
  organizationId,
  ...args
}: CreateTagArgs): Promise<UiTag | null> {
  const response = await post<ApiTag>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories/${categoryId}/tags`,
    mapTagToApi(args),
  );
  if (!response?.data) return null;
  return mapTagFromApi(locale, response?.data);
}

export async function editTagById({
  id,
  categoryId,
  locale,
  organizationId,
  ...args
}: EditTagByIdArgs): Promise<UiTag | null> {
  const response = await patch<ApiTag>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories/${categoryId}/tags/${id}`,
    mapTagToApi(args),
  );
  if (!response?.data) return null;
  return mapTagFromApi(locale, response?.data);
}
